import React, { useEffect, useState } from "react";
import axios from "axios";

interface Country { id: number; name: string; code: string; is_sensitive: boolean; active: boolean; }
interface Manager {
  id: number; username: string; full_name: string | null; role: string; active: boolean;
  countries: { id: number; name: string; code: string }[];
}

const TEAL = "#15403a";

/**
 * System-admin screen: provision countries (the tenancy boundary) and assign
 * managers to countries. Backend endpoints are system_admin-gated.
 */
export default function CountryAdmin() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [sensitive, setSensitive] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [c, m] = await Promise.all([axios.get("/api/countries"), axios.get("/api/managers")]);
    setCountries((c.data.data || c.data).countries || []);
    setManagers((m.data.data || m.data).managers || []);
    setLoading(false);
  };

  useEffect(() => { load().catch(() => { setMsg("Failed to load."); setLoading(false); }); }, []);

  const addCountry = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg("");
    try {
      await axios.post("/api/countries", { name, code, is_sensitive: sensitive });
      setName(""); setCode(""); setSensitive(false);
      await load();
    } catch (err: any) {
      setMsg(err?.response?.data?.error || "Failed to add country.");
    }
  };

  const assign = async (userId: number, countryId: number) => {
    if (!countryId) return;
    try { await axios.post(`/api/managers/${userId}/countries`, { country_id: countryId }); await load(); }
    catch { setMsg("Failed to assign."); }
  };

  const unassign = async (userId: number, countryId: number) => {
    try { await axios.delete(`/api/managers/${userId}/countries/${countryId}`); await load(); }
    catch { setMsg("Failed to unassign."); }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: 24 }}>
      <h2 style={{ color: TEAL }}>Countries &amp; Managers</h2>
      {msg && <div style={{ background: "#fdecec", color: "#a23", padding: "8px 12px", borderRadius: 6, marginBottom: 16 }}>{msg}</div>}

      {/* Countries */}
      <section style={{ marginBottom: 32 }}>
        <h3 style={{ color: TEAL, borderBottom: "2px solid #eef2f1", paddingBottom: 6 }}>Countries</h3>
        <form onSubmit={addCountry} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", margin: "12px 0" }}>
          <input placeholder="Country name" value={name} onChange={(e) => setName(e.target.value)} required
            style={{ padding: 8, border: "1px solid #cbd5d2", borderRadius: 5 }} />
          <input placeholder="code (e.g. kenya)" value={code} onChange={(e) => setCode(e.target.value)} required
            style={{ padding: 8, border: "1px solid #cbd5d2", borderRadius: 5, width: 150 }} />
          <label style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={sensitive} onChange={(e) => setSensitive(e.target.checked)} />
            Sensitive (hostile-tier)
          </label>
          <button type="submit" style={{ background: TEAL, color: "#fff", border: "none", padding: "9px 16px", borderRadius: 5, cursor: "pointer" }}>
            Add country
          </button>
        </form>
        {countries.length === 0 ? (
          <p style={{ color: "#718096" }}>No countries yet. Add one above.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead><tr style={{ textAlign: "left", color: "#718096" }}>
              <th style={{ padding: 6 }}>Name</th><th style={{ padding: 6 }}>Code</th><th style={{ padding: 6 }}>Tier</th>
            </tr></thead>
            <tbody>
              {countries.map((c) => (
                <tr key={c.id} style={{ borderTop: "1px solid #eef2f1" }}>
                  <td style={{ padding: 6 }}>{c.name}</td>
                  <td style={{ padding: 6, color: "#718096" }}>{c.code}</td>
                  <td style={{ padding: 6 }}>{c.is_sensitive ? <span style={{ color: "#b45309" }}>⚠ Sensitive</span> : "Standard"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Managers */}
      <section>
        <h3 style={{ color: TEAL, borderBottom: "2px solid #eef2f1", paddingBottom: 6 }}>Manager assignments</h3>
        <p style={{ color: "#718096", fontSize: 13 }}>A user can only see a country's data if assigned to it here — even system admins.</p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead><tr style={{ textAlign: "left", color: "#718096" }}>
            <th style={{ padding: 6 }}>User</th><th style={{ padding: 6 }}>Role</th><th style={{ padding: 6 }}>Assigned countries</th><th style={{ padding: 6 }}>Add</th>
          </tr></thead>
          <tbody>
            {managers.length === 0 && (
              <tr><td colSpan={4} style={{ padding: "12px 6px", color: "#a0aec0", fontStyle: "italic" }}>No managers found.</td></tr>
            )}
            {managers.map((m) => (
              <tr key={m.id} style={{ borderTop: "1px solid #eef2f1" }}>
                <td style={{ padding: 6 }}>{m.full_name || m.username}<div style={{ color: "#a0aec0", fontSize: 12 }}>{m.username}</div></td>
                <td style={{ padding: 6, color: "#718096" }}>{m.role}</td>
                <td style={{ padding: 6 }}>
                  {m.countries.length === 0 ? <span style={{ color: "#a0aec0" }}>none</span> : m.countries.map((c) => (
                    <span key={c.id} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#eef5f3", borderRadius: 12, padding: "2px 8px", marginRight: 6, marginBottom: 4 }}>
                      {c.name}
                      <button onClick={() => unassign(m.id, c.id)} title="Remove"
                        style={{ border: "none", background: "transparent", color: "#a23", cursor: "pointer", fontSize: 13, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </td>
                <td style={{ padding: 6 }}>
                  <select defaultValue="" onChange={(e) => { assign(m.id, parseInt(e.target.value, 10)); e.target.value = ""; }}
                    style={{ padding: 5, border: "1px solid #cbd5d2", borderRadius: 5 }}>
                    <option value="">+ country…</option>
                    {countries.filter((c) => !m.countries.some((mc) => mc.id === c.id)).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
